import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Icon from '../../../../../atoms/Icon'
import { useFormattingContext } from '../../../../../organisms/FormattingProvider/FormattingContext'
import { usePipelineChat } from './PipelineChatContext'

const useStyles = makeStyles(theme => ({
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: theme.spacing(0.75, 1.5),
    borderBottom: '1px solid var(--color-neutral-600)',
    backgroundColor: 'var(--surface-bg-color-d2)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    fontSize: '11px',
    color: 'var(--color-neutral-400)',
    flexWrap: 'wrap'
  },
  threadId: {
    fontFamily: 'var(--font-family-monospace)',
    fontSize: '11px',
    color: 'var(--text-color-main)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    '&:hover': {
      color: 'var(--color-primary-500)'
    }
  },
  threadIdIcon: {
    fontSize: '12px',
    opacity: 0.6
  },
  tokenUsage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '11px'
  },
  tokenUsageItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  modelName: {
    fontFamily: 'var(--font-family-monospace)',
    fontSize: '10px',
    color: 'var(--color-neutral-400)'
  },
  emptyState: {
    color: 'var(--color-neutral-500)',
    fontStyle: 'italic'
  }
}))

function ThreadHeader () {
  const classes = useStyles()
  const { threadId, threadData } = usePipelineChat()
  const { formatter } = useFormattingContext()
  const [copied, setCopied] = useState(false)

  const handleCopyThreadId = async () => {
    if (!threadId) return
    try {
      await navigator.clipboard.writeText(threadId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy thread ID:', error)
    }
  }

  const usageItems = useMemo(() => {
    const usage = threadData?.usage
    if (!usage || typeof usage !== 'object') {
      return []
    }

    const items = []
    for (const [model, tokens] of Object.entries(usage)) {
      const input = tokens?.inputTokens || 0
      const output = tokens?.outputTokens || 0
      if (input > 0 || output > 0) {
        items.push({ model, input, output })
      }
    }

    return items
  }, [threadData?.usage])

  if (!threadId) {
    return (
      <div className={classes.header}>
        <span className={classes.emptyState}>No active thread</span>
      </div>
    )
  }

  const shortThreadId = threadId.substring(0, 8)

  return (
    <div className={classes.header}>
      <div className={classes.threadId} onClick={handleCopyThreadId} title={`Thread ID: ${threadId} (click to copy)`}>
        <Icon name='link' className={classes.threadIdIcon} />
        <span>{shortThreadId}</span>
        {copied && <span style={{ fontSize: '10px', color: 'var(--color-success-500)' }}>Copied!</span>}
      </div>
      {usageItems.length > 0 && (
        <div className={classes.tokenUsage}>
          {usageItems.map(({ model, input, output }) => (
            <div key={model} className={classes.tokenUsageItem}>
              <span className={classes.modelName}>{model}</span>
              <span>⬆</span>
              <span>{formatter(input, 'human')}</span>
              <span>⬇</span>
              <span>{formatter(output, 'human')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ThreadHeader
